import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ChevronRight } from "lucide-react";
import { useActualites, useActualite } from "@/data/hooks";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp } from "@/components/Reveal";

const SITE_URL = "https://nanda-sa.com";

function useActuSeo(a, locale) {
  React.useEffect(() => {
    if (!a) return;
    const title = (a.seo && a.seo.title) || `${a.title} | NANDA SÁ`;
    const desc = (a.seo && a.seo.description) || a.excerpt;
    const canonical = `${SITE_URL}/actualites/${a.slug}`;
    const img = a.cover?.startsWith("http") ? a.cover : `${SITE_URL}${a.cover}`;

    document.title = title;
    const set = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    set("name", "description", desc);
    if (a.seo?.keywords) set("name", "keywords", a.seo.keywords);
    set("property", "og:type", "article");
    set("property", "og:title", title);
    set("property", "og:description", desc);
    set("property", "og:image", img);
    set("property", "og:url", canonical);
    set("name", "twitter:card", "summary_large_image");
    set("name", "twitter:title", title);
    set("name", "twitter:description", desc);
    set("name", "twitter:image", img);

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonical);
  }, [a, locale]);
}

export default function ActuPage() {
  const { locale } = useLanguage();
  const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);
  const dateLocale = locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR";
  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" }) : null;
  const { slug } = useParams();
  const a = useActualite(slug);
  const ACTUS = useActualites();

  useActuSeo(a, locale);

  if (!a) return <Navigate to="/actualites" replace />;

  const others = ACTUS.filter((x) => x.slug !== a.slug).slice(0, 2);
  const coverUrl = a.cover?.startsWith("http") ? a.cover : `${SITE_URL}${a.cover}`;

  const newsSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: a.title,
    description: a.excerpt,
    image: coverUrl,
    datePublished: a.date,
    dateModified: a.date,
    inLanguage: { fr: "fr-FR", en: "en-GB", pt: "pt-PT" }[locale],
    author: { "@type": "Organization", name: "NANDA SÁ" },
    publisher: {
      "@type": "Organization",
      name: "NANDA SÁ",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-nanda-sa.png` },
    },
    mainEntityOfPage: `${SITE_URL}/actualites/${a.slug}`,
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "NANDA SÁ", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: l("Actualités", "News", "Actualidades"), item: `${SITE_URL}/actualites` },
      { "@type": "ListItem", position: 3, name: a.title, item: `${SITE_URL}/actualites/${a.slug}` },
    ],
  };

  return (
    <div data-testid={`page-actu-${a.slug}`} className="bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(newsSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      {/* Header */}
      <section className="border-b border-nanda-line">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-44 pb-16 md:pb-20">
          <nav className="label-overline text-nanda-muted mb-10 flex items-center gap-2 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-nanda-ink transition-colors">{l("Accueil", "Home", "Início")}</Link>
            <ChevronRight size={11} strokeWidth={1.5} className="text-nanda-line" />
            <Link to="/actualites" className="hover:text-nanda-ink transition-colors">{l("Actualités", "News", "Actualidades")}</Link>
            <ChevronRight size={11} strokeWidth={1.5} className="text-nanda-line" />
            <span className="text-nanda-ink truncate max-w-[60vw]">{a.title}</span>
          </nav>

          <FadeUp>
            <div className="label-overline text-nanda-terracotta mb-6" data-testid="actu-meta">
              {a.universLabel} · {a.type}{a.date ? ` · ${formatDate(a.date)}` : ""}
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-sans font-light tracking-tight text-nanda-ink text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-balance max-w-4xl" data-testid="actu-title">
              {a.title}
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">{a.excerpt}</p>
          </FadeUp>
        </div>
      </section>

      {/* Cover */}
      <section>
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-14 md:py-20">
          <FadeUp>
            <div className="overflow-hidden rounded-3xl">
              <img src={a.cover} alt={a.coverAlt || a.title} loading="eager" className="w-full aspect-[16/9] object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Body */}
      <section>
        <div className="mx-auto max-w-3xl px-6 sm:px-12 pb-20 md:pb-28">
          {a.intro && (
            <p className="text-nanda-inkSoft text-lg md:text-xl font-light leading-[1.7] border-l-2 border-nanda-terracotta pl-6 whitespace-pre-line" data-testid="actu-intro">
              {a.intro}
            </p>
          )}
          {(a.sections || []).map((s, i) =>
            s.type === "h2" ? (
              <h2 key={i} id={s.id} className="font-sans font-light tracking-tight text-3xl md:text-4xl leading-[1.1] text-nanda-ink mt-14 mb-6 scroll-mt-24" data-testid={`actu-h2-${s.id || i}`}>
                {s.text}
              </h2>
            ) : (
              <p key={i} className="text-nanda-inkSoft text-base md:text-lg font-light leading-[1.8] mb-6 whitespace-pre-line">
                {s.text}
              </p>
            )
          )}

          {/* Gallery (photos réelles complémentaires) */}
          {a.gallery && a.gallery.length > 0 && (
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="actu-gallery">
              {a.gallery.map((g, i) => (
                <div key={i} className="overflow-hidden rounded-3xl border border-black/[0.06]">
                  <img src={g.src} alt={g.alt} loading="lazy" className="w-full aspect-[4/3] object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* CTA univers */}
          {a.cta && (
            <div className="mt-16 border-t border-nanda-line pt-10" data-testid="actu-cta">
              <Link
                to={a.cta.to}
                className="inline-flex items-center gap-2.5 bg-nanda-terracotta text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#8F5B3F] transition-colors duration-300"
                data-testid="actu-cta-link"
              >
                {a.cta.label}
                <ArrowUpRight size={15} strokeWidth={1.5} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Autres actualités */}
      {others.length > 0 && (
        <section className="border-t border-nanda-line bg-nanda-bgAlt">
          <div className="mx-auto max-w-7xl px-6 sm:px-12 py-20 md:py-28">
            <div className="label-overline text-nanda-muted mb-3">{l("À lire également", "Also read", "Ler também")}</div>
            <h2 className="font-sans font-light tracking-tight text-3xl md:text-4xl text-nanda-ink mb-12">
              {l("Dans les Actualités", "In the News", "Nas Actualidades")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {others.map((r) => (
                <Link key={r.slug} to={`/actualites/${r.slug}`} className="group block" data-testid={`actu-related-${r.slug}`}>
                  <div className="overflow-hidden rounded-3xl mb-6">
                    <img src={r.cover} alt={r.coverAlt || r.title} loading="lazy" className="w-full aspect-[16/10] object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out" />
                  </div>
                  <div className="label-overline text-nanda-muted">{r.universLabel}{r.date ? ` · ${formatDate(r.date)}` : ""}</div>
                  <h3 className="font-sans font-medium tracking-tight text-xl md:text-2xl text-nanda-ink mt-3 leading-tight group-hover:text-nanda-terracotta transition-colors duration-300">
                    {r.title}
                  </h3>
                </Link>
              ))}
            </div>
            <Link
              to="/actualites"
              className="mt-14 inline-flex items-center gap-2.5 text-sm font-medium text-nanda-ink link-underline"
              data-testid="actu-back-list"
            >
              <ArrowLeft size={15} strokeWidth={1.5} />
              {l("Toutes les actualités", "All news", "Todas as actualidades")}
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
